import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CalendarClock, MonitorSmartphone } from 'lucide-react';
import { ACTION, PAGE, PORTAL_NAME, SHIFT, shiftStatus, useTimezone } from '@kioskgaming/ui';
import CashierLayout from '../components/CashierLayout';
import PairingCodeForm from '../components/PairingCodeForm';
import { useCashierAuth } from '../hooks/useCashierAuth';
import { cashierListAssignedShifts, CashierShift } from '../services/cashierApi';
import { formatShiftWindow } from '../utils/formatShiftWindow';
import { canJoinShift, canStartShift, isShiftViewable, shiftNotViewableMessage } from '../utils/shiftAccess';
import { deviceStatusKind } from '../utils/startReadiness';

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-50 text-emerald-700';
    case 'scheduled':
      return 'bg-blue-50 text-blue-700';
    case 'auto_locked':
      return 'bg-amber-50 text-amber-700';
    case 'no_show':
      return 'bg-orange-50 text-orange-700';
    case 'cancelled':
      return 'bg-stone-100 text-stone-500';
    case 'pending_reconciliation':
    case 'pending_manager_review':
      return 'bg-rose-50 text-rose-700';
    default:
      return 'bg-stone-100 text-stone-700';
  }
}

/** The shift card's button text: Start shift (scheduled), Continue shift (active), else View shift. */
export function primaryActionLabel(status: string): string {
  if (canStartShift(status)) return SHIFT.startShift;
  if (canJoinShift(status)) return SHIFT.continueShift;
  return SHIFT.viewShift;
}

function getShiftAction(shift: CashierShift): { path: string; label: string } | null {
  if (!isShiftViewable(shift.status)) return null;
  const label = primaryActionLabel(shift.status);
  if (canStartShift(shift.status)) return { path: `/shifts/${shift.id}/start`, label };
  return { path: `/shifts/${shift.id}`, label };
}

/** "Starts on Counter 2" for a pinned scheduled shift, "Running on Counter 1" for an active one. */
function shiftDeviceLabel(shift: CashierShift): string | null {
  if (!shift.deviceName) return null;
  if (canStartShift(shift.status)) return `Starts on ${shift.deviceName}`;
  if (canJoinShift(shift.status)) return `Running on ${shift.deviceName}`;
  return null;
}

function ThisDeviceCard() {
  const { device, logout } = useCashierAuth();
  const navigate = useNavigate();
  const kind = deviceStatusKind(device);

  if (kind === 'approved') {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-emerald-800">
          <MonitorSmartphone className="h-4 w-4 shrink-0" />
          <span className="truncate">Approved · {device?.name || 'This device'}</span>
        </div>
        <span className="font-mono text-xs font-semibold text-emerald-700">Code {device?.shortCode}</span>
      </div>
    );
  }

  if (kind === 'pending') {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <MonitorSmartphone className="h-4 w-4 shrink-0" />
          <span>
            Waiting for approval · Code <span className="font-mono">{device?.shortCode}</span>
          </span>
        </div>
        <p className="mt-1 text-sm text-amber-900">
          Ask your manager to approve this device in the {PORTAL_NAME.shop} (Cashiers → Devices), or enter a pairing code.
        </p>
        <div className="mt-3">
          <PairingCodeForm />
        </div>
      </div>
    );
  }

  if (kind === 'blocked') {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800">
        <MonitorSmartphone className="h-4 w-4 shrink-0" />
        <span>This device is blocked. Ask your manager.</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-stone-200 bg-white px-4 py-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-stone-800">
        <MonitorSmartphone className="h-4 w-4 shrink-0" />
        <span>This device is not recognized. Sign in again.</span>
      </div>
      <button
        type="button"
        onClick={() => {
          logout();
          navigate('/login', { replace: true });
        }}
        className="rounded-lg border border-stone-200 px-3 py-1.5 text-sm font-semibold hover:bg-stone-50"
      >
        Sign in again
      </button>
    </div>
  );
}

export default function MyShiftsPage() {
  const { timezone } = useTimezone();
  const { refreshMe } = useCashierAuth();
  const [shifts, setShifts] = useState<CashierShift[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await cashierListAssignedShifts();
      if (!res.success) {
        toast.error(res.message || 'Failed to load shifts');
        setShifts([]);
        return;
      }
      setShifts(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <CashierLayout title={PAGE.myShifts} subtitle="All shifts for your account">
      <div className="mb-4">
        <ThisDeviceCard />
      </div>
      <div className="rounded-xl border border-stone-200 bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-stone-900">All shifts</div>
          <button
            type="button"
            onClick={() => {
              void refreshMe();
              void load();
            }}
            className="rounded-lg border border-stone-200 px-3 py-1.5 text-sm font-semibold hover:bg-stone-50"
          >
            {ACTION.refresh}
          </button>
        </div>

        <div className="mt-4">
          {loading ? (
            <div className="text-sm text-stone-600">Loading…</div>
          ) : shifts.length === 0 ? (
            <div className="rounded-lg border border-dashed border-stone-200 p-6 text-center text-sm text-stone-600">
              No shifts right now.
            </div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {shifts.map((shift) => (
              <div key={shift.id} className="rounded-lg border border-stone-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <CalendarClock className="h-4 w-4 text-stone-500" />
                      <span className="text-sm font-semibold text-stone-900">{formatShiftWindow(shift, timezone)}</span>
                    </div>
                    {shiftDeviceLabel(shift) ? (
                      <div className="mt-1 text-xs font-medium text-stone-700">{shiftDeviceLabel(shift)}</div>
                    ) : null}
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusBadgeClass(shift.status)}`}>
                    {shiftStatus(shift.status).label}
                  </span>
                </div>
                <div className="mt-3">
                  {(() => {
                    const action = getShiftAction(shift);
                    if (!action) {
                      return (
                        <p className="text-sm text-stone-600">{shiftNotViewableMessage(shift.status)}</p>
                      );
                    }
                    return (
                      <Link
                        to={action.path}
                        className="inline-flex rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                      >
                        {action.label}
                      </Link>
                    );
                  })()}
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </div>
    </CashierLayout>
  );
}
